import { useParams, Link } from "react-router-dom";
import { useMemo } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ArticleCard from "@/components/ArticleCard";
import Breadcrumbs from "@/components/Breadcrumbs";
import { categories } from "@/mocks/categories";
import { getArticlesByCategory } from "@/mocks/articles";
import type { Category } from "@/mocks/categories";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category: Category | undefined = useMemo(
    () => categories.find((c) => c.slug === slug),
    [slug]
  );

  const articles = useMemo(
    () => (slug ? getArticlesByCategory(slug) : []),
    [slug]
  );

  if (!category) {
    return (
      <>
        <SEOHead
          title="分类未找到"
          description="该分类不存在或已被移除，返回51吃瓜网首页浏览更多吃瓜资讯。"
          canonicalUrl="http://yuefenggroup.cn/"
        />
        <Navbar />
        <main className="min-h-[60vh] flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-foreground-950 mb-4">
              分类未找到
            </h1>
            <p className="text-foreground-600 mb-6">
              该分类不存在或已被移除
            </p>
            <Link
              to="/"
              className="px-6 py-3 bg-primary-500 text-background-50 font-semibold rounded-lg hover:bg-primary-600 transition-colors"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "首页",
        item: "http://yuefenggroup.cn/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: category.name,
        item: `http://yuefenggroup.cn/category/${category.slug}`,
      },
    ],
  };

  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: `${category.name} | 51吃瓜网`,
    url: `http://yuefenggroup.cn/category/${category.slug}`,
    description: category.description,
    isPartOf: {
      "@type": "WebSite",
      name: "51吃瓜网",
      url: "http://yuefenggroup.cn/",
    },
  };

  const keywordsMap: Record<string, string> = {
    "yule-bagua": "娱乐八卦,明星八卦,综艺爆料,影视秘闻,明星动态",
    "shehui-redian": "社会热点,民生话题,社会事件,热搜新闻,今日热点",
    "mingxing-heiliao": "明星黑料,明星翻车,深度扒皮,艺人黑历史,翻车实录",
    "wangluo-baoliao": "网络爆料,匿名爆料,论坛热帖,社交媒体,突发消息",
    "jinri-dagua": "今日大瓜,今日吃瓜,今日必吃,今天最热,实时爆料",
    "meiri-dasai": "每日大赛,网友投票,热门PK,票选活动,每日评选",
  };

  return (
    <>
      <SEOHead
        title={`${category.name} | 51吃瓜网今日吃瓜资源`}
        description={category.description}
        keywords={keywordsMap[category.slug] || "51吃瓜网,吃瓜资源,今日大瓜"}
        canonicalUrl={`http://yuefenggroup.cn/category/${category.slug}`}
        ogTitle={`${category.name} | 51吃瓜网`}
        ogDescription={category.description}
        ogImage={category.imageUrl}
        lastModified="2026-07-02"
        schemaData={[breadcrumbSchema, webPageSchema]}
      />

      <Navbar />

      <main>
        {/* Category Hero */}
        <section className="relative h-[300px] md:h-[400px] overflow-hidden">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${category.imageUrl})` }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950/70 via-background-950/40 to-background-950/30" />

          <div className="relative z-10 flex flex-col items-center justify-center h-full px-4 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/90 text-background-50 text-xl mb-4">
              <i className={category.icon} />
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-3">
              {category.name}
            </h1>
            <p className="text-base md:text-lg text-background-50/80 max-w-xl">
              {category.description}
            </p>
            <span className="mt-4 px-4 py-1.5 bg-background-50/10 backdrop-blur-sm rounded-full text-sm text-background-50/80">
              共 {category.articleCount} 篇内容
            </span>
          </div>
        </section>

        {/* Breadcrumbs & Content */}
        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="mb-8">
            <Breadcrumbs items={[{ label: category.name }]} />
          </div>

          {articles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {articles.map((article) => (
                <ArticleCard key={article.id} article={article} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <i className="ri-inbox-line text-5xl text-foreground-300 mb-4" />
              <h3 className="text-xl font-semibold text-foreground-700 mb-2">
                暂无内容
              </h3>
              <p className="text-foreground-500">
                该分类下暂时还没有文章，去看看其他分类吧
              </p>
            </div>
          )}
        </section>

        {/* Other Categories */}
        {articles.length > 0 && (
          <section className="w-full px-4 md:px-8 py-10 md:py-14 bg-background-100">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
              探索其他分类
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {categories
                .filter((c) => c.id !== category.id)
                .map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200 hover:border-primary-300 hover:-translate-y-0.5 transition-all"
                  >
                    <span className="w-10 h-10 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                      <i className={cat.icon} />
                    </span>
                    <div>
                      <div className="font-semibold text-foreground-900 text-sm">
                        {cat.name}
                      </div>
                      <div className="text-xs text-foreground-500">
                        {cat.articleCount} 篇
                      </div>
                    </div>
                  </Link>
                ))}
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}